'use client';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { ArrowUpIcon, TimeMachineIcon } from '@/components/icons/svg-icons';
import { matchStyles } from './match.style';
import { Button } from '@nf/ui';
import { cx } from 'styled-system/css';
import { MiniStreaming } from '../mini-streaming';
import { miniStreamingService } from '@/services/mini-streaming.service';
import { useObservableState, usePrevious } from '@nf/utils-common/hooks';
import { MatchModelBase, OutrightMarket, SportMatchModel } from '@nf/types/odds';
import { useSiteStore } from '@/store/site';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { useLocale, useTranslations } from 'next-intl';
import { useMatchBetTypes } from '@/features/odds-page/hooks/use-match-bet-types';
import { matchSome } from '@nf/utils-common/fpw/monadic';
import { getHomeTeamName, getTeamName, hasMoreAsianLines } from '@/utils/odds-utils';
import { TimeMachine } from '@/features/single-match/time-machine';
import { TimeMachineService } from '@/services/timemachine.service';
import { useAccountInfo } from '@/hooks/query/account';
import { MatchInfo } from '../odds-page-items';
import { AsianLinesProvider } from '../asian-lines/contexts/asian-lines-context';
import { AsianLines } from '../asian-lines';
import { MatchBettypes } from './match-bettype';
import { useRouter } from '@/navigation';
import { MatchOutright } from './match-outright';
import { CollapseMatchInfo } from '../collapse-match-info';
import { MatchSpecial } from '../match-special';
import { Scoreboard, ScoreboardSportId } from '@/features/sportsbook-aside/components/scoreboard';
import { scoreboardService } from '@/services/scoreboard.service';
import { useOddsTableMarsContext } from '../../context';
import { MoreBetTypesButton } from '../match-bet-types-button';
import { useCurrentProperties } from '@/hooks/query';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useAnalyseAttribute } from '@/features/next-analyse';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { KafkaNote } from '@nf/utils-common/constants';
import { SmpButton } from '@/features/single-match-parlay/components/smp-button';
import { sportsbookService } from '@/services/sportsbook.service';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';
import { useGetEnableMatchList } from '@/hooks/query/use-promotion-data';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useGoldenDuelSetting } from '@/hooks/query/golden-duel';

interface MatchProps {
	match: SportMatchModel;
	isDefaultExpand: boolean;
	isLeagueExpand: boolean;
};

const notPlayingStreamingMatchId = -1;

export const Match = ({ match, isDefaultExpand, isLeagueExpand }: MatchProps) => {
	const { sport, gameType } = useOddsTableMarsContext();
	const {
		MatchId,
		TeamId1,
		TeamId2,
		MaT,
		GameID,
		LeagueId,
		Moc,
	} = match;
	const [
		isParlayMode
	] = useSiteStore((state) => [
		state.isParlayMode
	]);
	const t = useTranslations('General');
	const { user } = useAccountInfo();
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const { eventMode, eventName } = useCurrentProperties();
	const oddsData = useAtomValue(oddsPageAtom);
	const { market, sport: sportFromUrl } = useCurrentProperties();
	const router = useRouter();
	const locale = useLocale();
	const [showMatch, setShowMatch] = useState(isDefaultExpand);
	const [isShowMiniStreaming, setIsShowMiniStreaming] = useState(false);
	const [showTimeMachine, setShowTimeMachine] = useState(false);
	const homeTeamName = getHomeTeamName(TeamId1, match.Neu, t('Neutral_Team'), oddsData?.teamNameMap);
	const awayTeamName = getTeamName(TeamId2, oddsData?.teamNameMap);
	const onPlayingStreamingMatchId = useObservableState(miniStreamingService.onPlayingStreamingChange$, notPlayingStreamingMatchId);
	const matchRef = useRef<HTMLDivElement>(null);
	const { hasOtherScoreboard } = scoreboardService.getScoreboardInfo(match);
	const { breakpoint } = useBreakpoint();
	const view = useAppearanceStore(state => state.view);
	const { data: jackpotEnableMatchList } = useGetEnableMatchList();
	const { isGoldenDuelSupported } = useGoldenDuelSetting();
	const { markets, hasGetMarket } = useMatchBetTypes({
		match: match,
		isFetchFromGetMarket: showMatch,
		sportId: sport,
		betTypeClass: 'OU'
	});
	const analyseTimeMachineAttributeRef = useAnalyseAttribute('use-time-machine-button');

	const hasMoreLines = showMatch && hasGetMarket && hasMoreAsianLines(sport, markets);

	useEffect(() => {
		setIsShowMiniStreaming(MatchId === onPlayingStreamingMatchId);
	}, [MatchId, onPlayingStreamingMatchId]);

	useEffect(() => {
		if (!showMatch) {
			setShowTimeMachine(false);
		}
	}, [showMatch]);

	useEffect(() => {
		if (isDefaultExpand) {
			setShowMatch(true);
		}
	}, [isDefaultExpand]);

	useEffect(() => {
		return (() => {
			// if match will unmount, close mini streaming
			if (isShowMiniStreaming) {
				setIsShowMiniStreaming(false);
				miniStreamingService.setPlayingStreamingMatchId(notPlayingStreamingMatchId);
			}
		})
	}, [sportFromUrl, market, isShowMiniStreaming])

	const toggleMatch = () => {
		const willShowMatch = !showMatch;

		if (!willShowMatch && isShowMiniStreaming) {
			setIsShowMiniStreaming(false);
			miniStreamingService.setPlayingStreamingMatchId(notPlayingStreamingMatchId);
		}

		setShowMatch(willShowMatch);
	}

	const toggleTimeMachine = () => {
		if (!showMatch) {
			setShowMatch(true);
		}
		if (view === ViewEnum.mars) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 6,
				action: 7
			}
			setKafkaNormal(kafkaData);
		}
		setShowTimeMachine(!showTimeMachine);
	}

	const setKafka = () => {
		if (view == ViewEnum.mars && breakpoint <= Breakpoint.sm) {
			setKafkaNormal({
				note: KafkaNote.MarsV1,
				type: 6,
				action: 34
			});
		}
	}

	const setPromotionKafka = () => {
		// jackpot
		if (siteConfig?.EnableSingleMatchJackpot && (jackpotEnableMatchList?.find(x => x.MatchId == MatchId) ?? false)) {
			setKafkaNormal({
				note: KafkaNote.EPLJackpot,
				type: view == ViewEnum.mars ? 2 : 1,
				action: breakpoint <= Breakpoint.sm ? 8 : 22
			});
		}

		// golden duel
		if (isGoldenDuelSupported(GameID, LeagueId)) {
			setKafkaNormal({
				note: KafkaNote.GoldenDuel,
				type: 1,
				action: 45
			});
		}
	}

	const handleVisitSingleMatch = () => {
		setKafka();
		setPromotionKafka();

		setIsShowMiniStreaming(false);
		miniStreamingService.setPlayingStreamingMatchId(notPlayingStreamingMatchId);
		const singleMatchHref = sportsbookService.makeSingleMatchPath({
			gameId: GameID,
			maT: MaT,
			leagueId: LeagueId,
			matchId: MatchId,
			eventMode: eventMode,
			eventName: eventName
		});
		router.push(singleMatchHref), { locale };
	}

	const canSeeTimeMachine = match?.GameID === 1
		&& TimeMachineService.HasTimeMachine(match as MatchModelBase, user)
		&& !isParlayMode;
	const isSpecialMatch = (sport: number) => [6, 9, 50, 8].includes(sport)
	const isOutrightSport = (sport: number) => sport === 999
	return (
		<AsianLinesProvider>
			<div ref={matchRef}>
				{
					matchSome(sport)
						.on(isOutrightSport, () => <MatchOutright match={match} markets={markets as OutrightMarket[]} teamNameMap={oddsData?.teamNameMap} />)
						.on(isSpecialMatch, () => (
							<>
								{
									isShowMiniStreaming && isLeagueExpand ?
										<MiniStreaming
											match={match}
											parrentRef={matchRef}
										/> : null
								}
								<MatchSpecial
									match={match}
									markets={markets}
									isShowMatch={showMatch}
									toggleMatch={toggleMatch}
									isLoading={!hasGetMarket}
									homeTeamName={homeTeamName}
									awayTeamName={awayTeamName}
								/>
							</>
						))
						.otherwise(() => (
							<div
								className={cx('match', matchStyles.match)}
							>
								{
									isShowMiniStreaming && isLeagueExpand ?
										<MiniStreaming
											match={match}
											parrentRef={matchRef}
										/> : null
								}
								<CollapseMatchInfo
									match={match}
									isOpen={showMatch}
									toggleMatch={toggleMatch}
									homeTeamName={homeTeamName}
									awayTeamName={awayTeamName}
									canSeeTimeMachine={canSeeTimeMachine}
									toggleTimeMachine={toggleTimeMachine}
								/>
								{/* Match 展開的資訊 */}
								{
									(showMatch || hasGetMarket) ? (
										<motion.div
											className={matchStyles.matchContent}
											initial="open"
											animate={showMatch ? 'open' : 'closed'}
											variants={{
												open: { height: 'auto', opacity: 1 },
												closed: { height: 0, opacity: 0 }
											}}
										>
											<MatchInfo match={match} />
											<div className={matchStyles.oddsTable}>
												<MatchBettypes
													match={match}
													markets={markets}
													isLoading={!hasGetMarket}
													hasMoreAsianLines={hasMoreLines}
													homeTeamName={homeTeamName}
													awayTeamName={awayTeamName}
												/>
											</div>
											<div className={matchStyles.moreOption}>
												<Button
													variant="icon"
													className={matchStyles.arrowButton}
													onClick={toggleMatch}
												>
													<ArrowUpIcon />
												</Button>
												<div className={matchStyles.iconButtons}>
													<MoreBetTypesButton moreCount={match.Moc + markets.length} clickHandler={handleVisitSingleMatch} />
													<SmpButton
														match={match}
														viewType={ViewEnum.mars}
														className={matchStyles.iconButton}
													/>

													{
														canSeeTimeMachine ? (
															<Button
																ref={analyseTimeMachineAttributeRef}
																variant="icon"
																className={matchStyles.iconButtonTimeMachine}
																data-open={showTimeMachine}
																onClick={toggleTimeMachine}
															>
																<TimeMachineIcon />
															</Button>
														) : null
													}
												</div>
											</div>
											{
												hasMoreLines && (
													<AsianLines
														markets={markets}
														match={match}
														homeTeamName={homeTeamName}
														awayTeamName={awayTeamName}
													/>
												)
											}
										</motion.div>
									) : null
								}
								{
									user && hasOtherScoreboard && <Scoreboard sportId={sport as ScoreboardSportId} matchModelData={match} />
								}
								{
									canSeeTimeMachine && showTimeMachine
										? <TimeMachine match={match} />
										: null
								}
							</div>
						))
				}
			</div>
		</AsianLinesProvider>
	)
}
